import { router } from "@inertiajs/react"
import { Pencil, Trash2 } from "lucide-react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import { DataTable, Td, Th, Thead, Tr } from "@/components/steel/table"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Field } from "@/components/steel"
import { Input } from "@/components/ui/input"

export type ProductionEntry = {
  id: string
  dbId: number
  date: string
  steelSqftPlaced: number | null
  wallSqftPlaced: number | null
  roofSqftPlaced: number | null
  steelHours: number | null
  wallHours: number | null
  roofHours: number | null
}

type EditForm = {
  entryDate: string
  steelSqftPlaced: string
  wallSqftPlaced: string
  roofSqftPlaced: string
  steelHours: string
  wallHours: string
  roofHours: string
}

const EDIT_FIELDS: { key: keyof Omit<EditForm, "entryDate">; label: string; unit: "sq ft" | "hrs" }[] = [
  { key: "steelSqftPlaced", label: "Steel", unit: "sq ft" },
  { key: "wallSqftPlaced", label: "Wall", unit: "sq ft" },
  { key: "roofSqftPlaced", label: "Roof", unit: "sq ft" },
  { key: "steelHours", label: "Steel", unit: "hrs" },
  { key: "wallHours", label: "Wall", unit: "hrs" },
  { key: "roofHours", label: "Roof", unit: "hrs" },
]

function sqft(value: number | null): string {
  return value != null ? value.toLocaleString() : "—"
}

function total(entry: ProductionEntry): number {
  return (entry.steelSqftPlaced ?? 0) + (entry.wallSqftPlaced ?? 0) + (entry.roofSqftPlaced ?? 0)
}

function totalHours(entry: ProductionEntry): number {
  return (entry.steelHours ?? 0) + (entry.wallHours ?? 0) + (entry.roofHours ?? 0)
}

export function ProductionEntryHistory({ entries }: { entries: ProductionEntry[] }) {
  const [editing, setEditing] = useState<ProductionEntry | null>(null)
  const [form, setForm] = useState<EditForm>({
    entryDate: "",
    steelSqftPlaced: "",
    wallSqftPlaced: "",
    roofSqftPlaced: "",
    steelHours: "",
    wallHours: "",
    roofHours: "",
  })

  const openEdit = (entry: ProductionEntry) => {
    setEditing(entry)
    setForm({
      entryDate: entry.date,
      steelSqftPlaced: entry.steelSqftPlaced != null ? String(entry.steelSqftPlaced) : "",
      wallSqftPlaced: entry.wallSqftPlaced != null ? String(entry.wallSqftPlaced) : "",
      roofSqftPlaced: entry.roofSqftPlaced != null ? String(entry.roofSqftPlaced) : "",
      steelHours: entry.steelHours != null ? String(entry.steelHours) : "",
      wallHours: entry.wallHours != null ? String(entry.wallHours) : "",
      roofHours: entry.roofHours != null ? String(entry.roofHours) : "",
    })
  }

  const sorted = [...entries].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())

  return (
    <div>
      <h2 className="mb-3 text-title font-semibold">Progress history</h2>

      {sorted.length === 0 ? (
        <p className="border-t border-border py-8 text-center text-label text-muted-foreground">
          No entries yet. Save one above to start the history.
        </p>
      ) : (
        <DataTable minWidth={560}>
          <Thead>
            <Th>Date</Th>
            <Th align="right">Steel</Th>
            <Th align="right">Wall</Th>
            <Th align="right">Roof</Th>
            <Th align="right">Total</Th>
            <Th align="right">Hours</Th>
            <Th />
          </Thead>
          <tbody>
            {sorted.map((entry) => (
              <Tr key={entry.id}>
                <Td num strong>
                  {entry.date}
                </Td>
                <Td align="right" num muted>
                  {sqft(entry.steelSqftPlaced)}
                </Td>
                <Td align="right" num muted>
                  {sqft(entry.wallSqftPlaced)}
                </Td>
                <Td align="right" num muted>
                  {sqft(entry.roofSqftPlaced)}
                </Td>
                <Td align="right" num strong>
                  {total(entry).toLocaleString()} sq ft
                </Td>
                <Td align="right" num muted>
                  {totalHours(entry) > 0 ? `${totalHours(entry).toLocaleString()} hrs` : "—"}
                </Td>
                <Td align="right" className="whitespace-nowrap">
                  <Button variant="ghost" size="icon-sm" onClick={() => openEdit(entry)}>
                    <Pencil />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => router.delete(`/production_entries/${entry.dbId}`, { preserveScroll: true })}
                  >
                    <Trash2 />
                  </Button>
                </Td>
              </Tr>
            ))}
          </tbody>
        </DataTable>
      )}

      <Dialog open={editing != null} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit entry</DialogTitle>
            <DialogDescription>
              Production metrics and the finish projection recalculate on save.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <Field label="Date">
              <Input
                type="date"
                value={form.entryDate}
                onChange={(e) => setForm((prev) => ({ ...prev, entryDate: e.target.value }))}
              />
            </Field>
            <div className="grid grid-cols-3 gap-2.5">
              {EDIT_FIELDS.map(({ key, label, unit }) => (
                <Field key={key} label={`${label} (${unit})`}>
                  <Input
                    className="num"
                    type="number"
                    min="0"
                    placeholder="0"
                    value={form[key]}
                    onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.value }))}
                  />
                </Field>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => {
                if (!editing || !form.entryDate) return
                router.patch(
                  `/production_entries/${editing.dbId}`,
                  {
                    entryDate: form.entryDate,
                    steelSqftPlaced: form.steelSqftPlaced || null,
                    wallSqftPlaced: form.wallSqftPlaced || null,
                    roofSqftPlaced: form.roofSqftPlaced || null,
                    steelHours: form.steelHours || null,
                    wallHours: form.wallHours || null,
                    roofHours: form.roofHours || null,
                  },
                  { preserveScroll: true, onSuccess: () => setEditing(null) },
                )
              }}
            >
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
